import { Fragment, useEffect, useMemo, useState } from "react"
import {
  IActivityLevel,
  IGoalTypeName,
  IOnboardingForm,
  ISetGoalPayload,
  ISetGoalStep,
} from "../types"
import SetActivityLevel from "./Steps/SetActivityLevel"
import SetTypeName from "./Steps/SetTypeName"
import {
  CTABox,
  OnboardingContent,
  OnboardingWrapper,
  ToPersonalLink,
} from "../styled"
import { HeaderBackBox, HeaderBackButton, OnboardingHeader } from "./styled"
import Button from "components/Buttons/Button"
import { useOnboarding } from "../hooks"
import PATHS from "router/paths"
import Stepper from "../../../components/onboarding/Stepper"
import SetTargetWeight from "./Steps/SetTargetWeight"
import Plan from "./Steps/ReviewPlan"
import { useSetPageTitle } from "libs/ga"
import { MAINTAIN_WEIGHT_TYPES } from "config"
import ChevronLeftBold from "components/Icons/ChevronLefBold"
import { useBackToHome } from "features/meal/hooks"
import { useBody, useBodyDisplayUnits } from "features/profile/hooks/body"
import { getGramWeight } from "utils/heightConverter"
import {
  getBodyWeightValueFromGram,
  getMaxBodyWeight,
  getMinBodyWeight,
} from "utils/weightConverter"

const Onboarding = () => {
  useSetPageTitle("Set Goal - Onboarding")
  const { body } = useBody()
  const { bodyDisplayUnits } = useBodyDisplayUnits()

  const [step, setStep] = useState(ISetGoalStep.SetActivityLevel)

  const currentBodyWeight = body.weight

  const currentWeightInDisplayUnit = getBodyWeightValueFromGram(
    bodyDisplayUnits,
    currentBodyWeight,
  )

  const [values, setValues] = useState<IOnboardingForm>({
    activityLevel: "ACTIVE",
    typeName: "WEIGHT_LOSS",
    weightChangePerWeek: 0.5,
    targetWeight: currentWeightInDisplayUnit,
    height: body.height,
    weight: body.weight,
  })

  useEffect(() => {
    setValues({
      ...values,
      targetWeight: getBodyWeightValueFromGram(
        bodyDisplayUnits,
        currentBodyWeight,
      ),
    })
  }, [currentBodyWeight, bodyDisplayUnits])

  const { handleOnboarding, onboardingLoading } = useOnboarding(() => {
    setStep(ISetGoalStep.ReviewPlan)
  })

  const backToHome = useBackToHome()

  const handleWeightChange = (targetWeight: number) => {
    setValues((val) => ({ ...val, targetWeight }))
  }

  const hiddenGoalTypes = useMemo(() => {
    const hidden: IGoalTypeName[] = []
    const minBodyWeight = getMinBodyWeight(bodyDisplayUnits)
    const maxBodyWeight = getMaxBodyWeight(bodyDisplayUnits)

    if (currentWeightInDisplayUnit <= minBodyWeight) {
      hidden.push("WEIGHT_LOSS")
    }
    if (currentWeightInDisplayUnit >= maxBodyWeight) {
      hidden.push("BUILDING")
    }

    return hidden
  }, [bodyDisplayUnits, currentWeightInDisplayUnit])

  useEffect(() => {
    if (hiddenGoalTypes.includes(values.typeName)) {
      const defaultType = hiddenGoalTypes.includes("WEIGHT_LOSS")
        ? "BUILDING"
        : "WEIGHT_LOSS"
      setValues((val) => ({ ...val, typeName: defaultType }))
    }
  }, [hiddenGoalTypes])

  const getPayload = (isMaintain?: boolean) => {
    let payload: ISetGoalPayload = {
      activityLevel: values.activityLevel,
      typeName: values.typeName,
      height: body.height,
      weight: body.weight,
    }

    if (!isMaintain) {
      const gramTargetWeight = getGramWeight(
        bodyDisplayUnits,
        values.targetWeight,
      )
      const gramWeightChangePerWeek = getGramWeight(
        bodyDisplayUnits,
        values.weightChangePerWeek,
      )

      payload = {
        ...payload,
        weightChangePerWeek: gramWeightChangePerWeek,
        targetWeight: gramTargetWeight,
      }
    }

    return payload
  }

  const goNext = () => {
    switch (step) {
      case ISetGoalStep.SetActivityLevel:
        setStep(ISetGoalStep.SetTypeName)
        break

      case ISetGoalStep.SetTypeName:
        const isMaintain = MAINTAIN_WEIGHT_TYPES.includes(values.typeName)
        if (isMaintain) {
          handleOnboarding(getPayload(true))
        } else {
          setStep(ISetGoalStep.SetTargetWeight)
        }
        break

      case ISetGoalStep.SetTargetWeight:
        handleOnboarding(getPayload(false))
        break

      default:
        break
    }
  }

  const onBack = () => {
    if (onboardingLoading) {
      return
    }

    const isPreviewMaintainPlan =
      step === ISetGoalStep.ReviewPlan &&
      MAINTAIN_WEIGHT_TYPES.includes(values.typeName)

    setStep((step) => {
      if (isPreviewMaintainPlan) {
        return step - 2
      }

      return step - 1
    })
  }

  const isDisableGoNextStep =
    step === ISetGoalStep.SetTargetWeight &&
    values.targetWeight === currentWeightInDisplayUnit

  if (step === ISetGoalStep.ReviewPlan) {
    return <Plan onBack={onBack} onGetStarted={backToHome} />
  }

  return (
    <OnboardingWrapper>
      <OnboardingHeader>
        {step === ISetGoalStep.SetActivityLevel ? (
          <HeaderBackBox>
            <ToPersonalLink to={PATHS.app.personalInfo}>
              <HeaderBackButton>
                <ChevronLeftBold fill="#4BD9DE" />
              </HeaderBackButton>
            </ToPersonalLink>
          </HeaderBackBox>
        ) : (
          <HeaderBackBox>
            <HeaderBackButton $disabled={onboardingLoading} onClick={onBack}>
              <ChevronLeftBold fill="#4BD9DE" isDisabled={onboardingLoading} />{" "}
            </HeaderBackButton>
          </HeaderBackBox>
        )}
        <Stepper currentStep={step} />
      </OnboardingHeader>

      <OnboardingContent>
        {step === ISetGoalStep.SetActivityLevel && (
          <SetActivityLevel
            value={values.activityLevel}
            onChange={(activityLevel: IActivityLevel) =>
              setValues((val) => ({ ...val, activityLevel }))
            }
          />
        )}

        {step === ISetGoalStep.SetTypeName && (
          <SetTypeName
            value={values.typeName}
            onChange={(typeName) => {
              setValues((val) => ({ ...val, typeName }))
            }}
            hiddenGoalTypes={hiddenGoalTypes}
          />
        )}

        {step === ISetGoalStep.SetTargetWeight && (
          <Fragment>
            <SetTargetWeight
              targetWeight={values.targetWeight}
              weightChangePerWeek={values.weightChangePerWeek}
              onWeightChangePerWeekChange={(nextWeightChangePerWeek) => {
                setValues((val) => ({
                  ...val,
                  weightChangePerWeek: nextWeightChangePerWeek,
                }))
              }}
              onTargetWeightChange={handleWeightChange}
              typeName={values.typeName as "WEIGHT_LOSS" | "BUILDING"}
              bodyDisplayUnits={bodyDisplayUnits}
              currentWeightInGrams={body.weight}
            />
          </Fragment>
        )}
      </OnboardingContent>

      <CTABox style={{ paddingLeft: 24, paddingRight: 24 }}>
        <Button
          $type="primary"
          style={{ width: "100%" }}
          loading={onboardingLoading}
          onClick={goNext}
          disabled={isDisableGoNextStep}
          $theme="dark"
          $borderLess
        >
          Continue
        </Button>
      </CTABox>
    </OnboardingWrapper>
  )
}

export default Onboarding
